import React, { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import Pagination from '@mui/material/Pagination';
import Stack from '@mui/material/Stack';
import {
  createColumnHelper,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  type PaginationState,
  useReactTable,
} from '@tanstack/react-table';
import { format } from 'date-fns';
import { ChevronLeft, ChevronRight } from 'lucide-react';

import {
  useGetOrgAuditLogs,
  useGetOrgMembers,
} from '../../hooks/tanstack/useOrganizationHook';
import {
  useGetRouteParams,
  useGetSearchResults,
} from '../../hooks/tanstack/useUtilsHook';
import AuditLogDialog from '../AuditLogDialog';
import FormSelect from '../form/FormSelect';
import CustomMenuDropdown from '../model/CustomMenuDropdown';
import SearchInput from '../SearchInput';
import TanstackTable from '../TanstackTable';

export type DataRow = {
  id: number;
  email: string;
  timestamp: string | null;
  action: string;
  description: string;
};

const columnHelper = createColumnHelper<DataRow>();

const columns = [
  columnHelper.accessor('email', {
    cell: (info) => {
      const email = info.getValue();

      return <p className="text-sm w-[200px] text-[#666]">{email}</p>;
    },
    header: () => (
      <div className="flex items-center gap-1">
        <p>User</p>
      </div>
    ),
    size: 200,
  }),
  columnHelper.accessor('timestamp', {
    id: 'timestamp',
    cell: (info) =>
      info.getValue() ? (
        <div className="w-[150px]">
          {format(info.getValue() as string, 'y/MM/dd h:mm aaa')}
        </div>
      ) : (
        <div className="text-gray-400 w-[200px] text-start">--</div>
      ),
    header: () => <div>Timestamp</div>,
    size: 200,
  }),
  columnHelper.accessor('action', {
    id: 'action',
    cell: (info) => {
      const action = info.getValue();

      return <div className="uppercase">{action}</div>;
    },
    header: () => <div>Action</div>,
  }),
  columnHelper.accessor('description', {
    id: 'description',
    cell: (info) => {
      const description = info.getValue();

      return (
        <div className="capitalize break-all line-clamp-1 w-[300px]">
          {description}
        </div>
      );
    },
    header: () => <div>Description</div>,
    size: 450,
  }),
];

const timestamps = [
  {
    value: '7d',
    label: '7 days ago',
  },
  {
    value: '30d',
    label: '1 months ago',
  },
  {
    value: '90d',
    label: '3 months ago',
  },
];

const actions = [
  {
    value: 'create',
    label: 'CREATE',
  },
  {
    value: 'update',
    label: 'UPDATE',
  },
  {
    value: 'delete',
    label: 'DELETE',
  },
];

const AuditLogView = () => {
  const [data, setData] = useState<DataRow[]>([]);

  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });

  const [openAuditLogDialog, setOpenAuditLogDialog] = useState<{
    row: DataRow | null;
    open: boolean;
  }>({
    row: null,
    open: false,
  });

  const form = useForm();

  const { watch } = form;

  const { orgId } = useGetRouteParams();

  const searchText = watch('search');

  const searchResults = useGetSearchResults<DataRow>({
    searchValue: searchText,
    data,
    filterFields: ['action', 'actor', 'object_repr', 'changes_str'],
  });

  const timestamp = watch('timestamp');
  const action = watch('action');
  const actor = watch('users');

  const {
    data: auditLog,
    isLoading: isFetchingAuditLogs,
    isRefetching: isRefetchingAuditLogs,
  } = useGetOrgAuditLogs(
    {
      orgId,
      params: {
        page: (pagination.pageIndex + 1).toString(),
        actor: actor
          ? Object.keys(actor)
              .filter((key) => actor[key])
              .join(',')
          : '',
        action: action
          ? Object.keys(action)
              .filter((key) => action[key])
              .flatMap((a) => a.toUpperCase())
              .join(',')
          : '',
        timestamp: timestamp ?? '',
      },
    },
    {
      enabled: !!orgId,
    }
  );

  const { data: members } = useGetOrgMembers(
    {
      orgId,
    },
    {
      enabled: !!orgId,
    }
  );

  const orgUsers =
    members?.map((member) => ({
      label: member.user.full_name,
      value: member.user.id,
    })) ?? [];

  useEffect(() => {
    if (!auditLog?.results) {
      return;
    }

    const dataRows =
      auditLog.results.map((log, index) => ({
        id: index,
        email: log.actor.email,
        timestamp: log.timestamp,
        action: log.action,
        description: log.object_repr.concat(' ', log.changes_str),
      })) ?? [];

    setData(dataRows);
  }, [auditLog?.results]);

  const rowCount = auditLog?.count ?? 0;

  const table = useReactTable({
    data: searchResults ?? data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    rowCount,
    onPaginationChange: setPagination,
    manualPagination: true,
    autoResetPageIndex: false,
    state: {
      sorting: [
        {
          id: 'timestamp',
          desc: true,
        },
      ],
      pagination,
    },
    debugTable: true,
  });

  const handleChange = (event: React.ChangeEvent<unknown>, value: number) => {
    table.setPageIndex(value - 1);
  };

  const handleOpenAuditLogDialog = (id: number) => {
    const row = table.getRowModel().rows[id];
    const rowData = row.original as DataRow;

    if (!rowData) {
      return;
    }

    setOpenAuditLogDialog({
      row: rowData,
      open: true,
    });
  };

  const handleCloseAuditLogDialog = () => {
    setOpenAuditLogDialog({
      row: null,
      open: false,
    });
  };

  const isLoading = isFetchingAuditLogs || isRefetchingAuditLogs;

  return (
    <div className="mx-auto flex max-w-lg 3xl:max-w-2xl flex-col space-y-6 py-6">
      <div className="flex w-full justify-between items-center">
        <div className="flex flex-col gap-2">
          <h1 className="text-2xl font-bold text-[#333]">Audit Log</h1>
        </div>
      </div>
      <div className="w-full">
        <FormProvider {...form}>
          <form className="flex flex-col">
            <div className="flex gap-3 flex-wrap">
              <div className="w-full max-w-[300px] ">
                <SearchInput />
              </div>
              {orgUsers && (
                <CustomMenuDropdown
                  name={'users'}
                  label={'User'}
                  data={orgUsers}
                />
              )}
              <div>
                <FormSelect
                  name={'timestamp'}
                  placeholder={'Timestamp'}
                  defaultValue={'7d'}
                  options={timestamps}
                />
              </div>
              {actions && (
                <CustomMenuDropdown
                  name={'action'}
                  label={'Action'}
                  data={actions}
                />
              )}
              <div />
            </div>
            <div className="py-4 relative">
              <TanstackTable
                table={table}
                isLoading={isLoading}
                handleSelect={{
                  onClick: handleOpenAuditLogDialog,
                }}
                emptyState={searchText ? 'No results' : 'No activities yet.'}
              />
              <AuditLogDialog
                open={openAuditLogDialog.open}
                content={
                  <div className="flex flex-col gap-4">
                    <div className="flex justify-between items-center">
                      <div className="flex items-center gap-2">
                        <div className="text-[#999]">User:</div>
                        <div>{openAuditLogDialog.row?.email}</div>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className=" text-[#999]">Action:</div>
                        <div>{openAuditLogDialog.row?.action}</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className=" text-[#999]">Timestamp:</div>
                      <div>{openAuditLogDialog.row?.timestamp}</div>
                    </div>
                    <div>
                      <span className="text-[#999]">Description: </span>
                      {openAuditLogDialog.row?.description}
                    </div>
                  </div>
                }
                handleOnClose={handleCloseAuditLogDialog}
              />
              {!isLoading && (
                <div className="flex items-center px-4 py-2 text-[#666] w-full gap-2 justify-between border-t-0 border border-gray-300">
                  <div>
                    Showing {table.getRowModel().rows.length.toLocaleString()}{' '}
                    of {table.getRowCount().toLocaleString()} Rows
                  </div>
                  <div className="flex items-center">
                    <button
                      className="px-3"
                      type="button"
                      onClick={() => table.previousPage()}
                      disabled={!table.getCanPreviousPage()}
                    >
                      <ChevronLeft size={20} />
                    </button>
                    <Stack spacing={2}>
                      <Pagination
                        shape="rounded"
                        count={table.getPageCount()}
                        page={pagination.pageIndex + 1}
                        onChange={handleChange}
                        hidePrevButton
                        hideNextButton
                        sx={{
                          '& .Mui-focusVisible': {
                            backgroundColor: '#FFFAF9 !important',
                            color: '#B8341B',
                          },
                          '& .Mui-selected': {
                            backgroundColor: '#FFFAF9 !important',
                            color: '#B8341B',
                          },
                        }}
                      />
                    </Stack>
                    <button
                      className="px-3"
                      type="button"
                      onClick={() => table.nextPage()}
                      disabled={!table.getCanNextPage()}
                    >
                      <ChevronRight size={20} />
                    </button>
                  </div>
                </div>
              )}
            </div>
          </form>
        </FormProvider>
        <div className="h-4" />
      </div>
    </div>
  );
};

export default AuditLogView;
